// WorkspaceSidebar — Topic info, Bloom overview, and mode selection
//
// Displays topic title/description, aggregated Bloom level from concept map,
// and three mode buttons with contextual hints based on learning progress.

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { TeachingForm, BloomLevel } from '../../types';
import type { Node } from '@xyflow/react';

interface WorkspaceSidebarProps {
  topicTitle: string;
  topicDescription?: string;
  conceptMapNodes: Node[];
  subtopicTouchCount: number;
  isCreating: boolean;
  journeyId: string;
  topicId: string;
  onSelectMode: (form: TeachingForm) => void;
  examSessions?: Array<{ teachingForm?: string; bloomLevelReached?: string }>;
}

const BLOOM_ORDER: BloomLevel[] = ['recognize', 'understand', 'apply', 'analyze', 'evaluate', 'create'];

const BLOOM_COLORS: Record<BloomLevel, string> = {
  recognize: 'bg-gray-500',
  understand: 'bg-blue-500',
  apply: 'bg-green-500',
  analyze: 'bg-yellow-500',
  evaluate: 'bg-orange-500',
  create: 'bg-red-500',
};

interface ModeConfig {
  form: TeachingForm;
  emoji: string;
  colorBg: string;
  colorBorder: string;
  colorText: string;
}

const MODES: ModeConfig[] = [
  { form: 'introduction', emoji: '🌱', colorBg: 'bg-amber-950/30', colorBorder: 'border-amber-500', colorText: 'text-amber-400' },
  { form: 'dialog', emoji: '💬', colorBg: 'bg-blue-950/30', colorBorder: 'border-blue-500', colorText: 'text-blue-400' },
  { form: 'exam', emoji: '🎯', colorBg: 'bg-indigo-950/30', colorBorder: 'border-indigo-500', colorText: 'text-indigo-400' },
];

function getHint(
  form: TeachingForm,
  touchCount: number,
  bloomDistribution: Record<BloomLevel, number>,
  t: (key: string) => string,
): string | null {
  const total = Object.values(bloomDistribution).reduce((a, b) => a + b, 0);
  if (total === 0 && touchCount === 0) {
    return form === 'introduction' ? t('workspace.hints.startHere') : null;
  }

  const lowerCount = (bloomDistribution.recognize || 0) + (bloomDistribution.understand || 0);
  const upperCount = total - lowerCount;

  if (form === 'dialog' && lowerCount > upperCount) {
    return t('workspace.hints.recommended');
  }
  if (form === 'exam' && upperCount >= lowerCount && total > 0) {
    return t('workspace.hints.readyFor');
  }
  return null;
}

type ExamReadiness = 'notPracticed' | 'started' | 'practiced';

function getExamReadiness(
  sessions: Array<{ teachingForm?: string; bloomLevelReached?: string }>
): ExamReadiness {
  const examSessions = sessions.filter(s => s.teachingForm === 'exam');
  if (examSessions.length === 0) return 'notPracticed';

  const bloomOrder = ['recognize', 'understand', 'apply', 'analyze', 'evaluate', 'create'];
  const hasHighBloom = examSessions.some(s => {
    const idx = bloomOrder.indexOf(s.bloomLevelReached || '');
    return idx >= 2; // 'apply' or higher
  });

  return hasHighBloom ? 'practiced' : 'started';
}

const EXAM_READINESS_STYLES: Record<ExamReadiness, { dot: string; text: string }> = {
  notPracticed: { dot: 'bg-gray-600',   text: 'text-gray-400' },
  started:      { dot: 'bg-yellow-400', text: 'text-yellow-400' },
  practiced:    { dot: 'bg-green-500',  text: 'text-green-400' },
};

function getBloomDistribution(nodes: Node[]): Record<BloomLevel, number> {
  const dist: Record<BloomLevel, number> = {
    recognize: 0, understand: 0, apply: 0, analyze: 0, evaluate: 0, create: 0,
  };
  for (const node of nodes) {
    const level = (node.data as { bloomLevel?: BloomLevel })?.bloomLevel;
    if (level && level in dist) {
      dist[level]++;
    }
  }
  return dist;
}

export default function WorkspaceSidebar({
  topicTitle,
  topicDescription,
  conceptMapNodes,
  subtopicTouchCount,
  isCreating,
  journeyId,
  topicId,
  onSelectMode,
  examSessions,
}: WorkspaceSidebarProps) {
  const { t } = useTranslation(['session', 'teaching']);
  const navigate = useNavigate();
  const bloomDist = getBloomDistribution(conceptMapNodes);
  const totalNodes = conceptMapNodes.length;

  return (
    <div className="h-full bg-gray-900 border-l border-gray-800 p-6 flex flex-col overflow-y-auto">
      {/* Topic info */}
      <div className="mb-6">
        <h1 className="text-xl font-display font-semibold text-white mb-2">
          {topicTitle}
        </h1>
        {topicDescription && (
          <p className="text-sm text-gray-400 leading-relaxed">
            {topicDescription}
          </p>
        )}
      </div>

      {/* Bloom overview */}
      {totalNodes > 0 && (
        <div className="mb-6">
          <h2 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">
            {t('workspace.bloomOverview')}
          </h2>
          <div className="flex gap-1 h-2 rounded-full overflow-hidden bg-gray-800">
            {BLOOM_ORDER.map(level => {
              const count = bloomDist[level];
              if (count === 0) return null;
              const pct = (count / totalNodes) * 100;
              return (
                <div
                  key={level}
                  className={`${BLOOM_COLORS[level]} transition-all duration-300`}
                  style={{ width: `${pct}%` }}
                  title={`${level}: ${count}`}
                />
              );
            })}
          </div>
          <div className="mt-1 text-xs text-gray-500">
            {t('workspace.conceptCount', { count: totalNodes })}
          </div>
          {examSessions && (
            <div className="flex items-center gap-2 mt-2" title={t(`teaching:examReadiness.${getExamReadiness(examSessions)}`)}>
              <span className={`w-2 h-2 rounded-full ${EXAM_READINESS_STYLES[getExamReadiness(examSessions)].dot}`} />
              <span className={`text-xs ${EXAM_READINESS_STYLES[getExamReadiness(examSessions)].text}`}>
                {t(`teaching:examReadiness.${getExamReadiness(examSessions)}`)}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Mode selection */}
      <div className="mt-auto">
        <h2 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">
          {t('workspace.pickMode')}
        </h2>
        <div className="flex flex-col gap-2">
          {MODES.map(({ form, emoji, colorBg, colorBorder, colorText }) => {
            const hint = getHint(form, subtopicTouchCount, bloomDist, t);
            return (
              <button
                key={form}
                onClick={() => onSelectMode(form)}
                disabled={isCreating}
                className={`${colorBg} border ${colorBorder} rounded-xl p-3 text-left transition-all hover:brightness-125 disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <span className={`text-sm font-medium ${colorText}`}>
                      {emoji} {t(`forms.${form}.title`)}
                    </span>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {t(`forms.${form}.description`)}
                    </p>
                  </div>
                  {hint && (
                    <span className={`text-xs ${colorText} bg-white/5 px-2 py-0.5 rounded-full`}>
                      {hint}
                    </span>
                  )}
                </div>
                {isCreating && (
                  <div className="mt-2 flex items-center gap-2">
                    <div className="animate-spin rounded-full h-3 w-3 border-b border-current" />
                    <span className="text-xs text-gray-500">{t('workspace.creatingSession')}</span>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        <button
          type="button"
          onClick={() => navigate(`/preparation/${journeyId}/${topicId}`)}
          className="w-full mt-4 py-2 px-3 text-sm text-gray-400 hover:text-gray-200 transition-colors flex items-center gap-2"
        >
          <span aria-hidden="true">📝</span>
          {t('workspace.openPreparation')}
        </button>
      </div>
    </div>
  );
}
